import React, { useEffect, useRef } from 'react';

interface MatrixCodeRainProps {
  className?: string;
  opacity?: number;
  speed?: number; // lower is slower and more gentle
  colorMode?: 'gold-amber' | 'bronze-gold' | 'burnt-brown';
}

export const MatrixCodeRain: React.FC<MatrixCodeRainProps> = ({
  className = '',
  opacity = 0.75,
  speed = 0.45,
  colorMode = 'gold-amber',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Skip entirely on mobile viewports (< 768px) to guarantee 60fps GPU rendering in mobile Chrome
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animationFrameId: number;
    let startTimerId: ReturnType<typeof setTimeout>;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    let displayWidth = 400;
    let displayHeight = 600;

    function resizeCanvas(width?: number, height?: number) {
      if (!canvas) return;
      if (width && height) {
        displayWidth = width;
        displayHeight = height;
      } else if (canvas.parentElement) {
        displayWidth = canvas.parentElement.clientWidth || 400;
        displayHeight = canvas.parentElement.clientHeight || 600;
      }

      canvas.width = Math.floor(displayWidth * dpr);
      canvas.height = Math.floor(displayHeight * dpr);
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      ctx?.scale(dpr, dpr);
      initColumns();
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          resizeCanvas(Math.floor(entry.contentRect.width), Math.floor(entry.contentRect.height));
        }
      }
    });

    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    // Meaningful code tokens and characters matching Web Architecture, MQL5, C++, Algorithms & SEO
    const codeTokens = [
      'const', 'let', 'async', 'await', 'return', 'export', 'import', 'function',
      'class', 'void', 'int', 'double', 'float', 'string', 'struct', 'enum',
      'trade()', 'OnInit()', 'OnTick()', 'strategy.entry()', 'ta.rsi()', 'plot()',
      '0xFA', '0x86', '0x49', '0x08', '0x12', '10101', '11001',
      '{...}', '=>', '===', '!==', '&&', '||', '++', '--',
      'SEO.audit()', 'speed: 99%', 'SSL: OK', 'react.tsx', 'api.fetch()',
      'sorena', 'IT.core', 'db.query()', 'next.js', 'std::cout',
      'BUY', 'SELL', 'PROFIT', 'AI.agent', 'algo.run()', '0', '1', '<Code />',
      '#include', 'namespace', 'typedef', 'template<>'
    ];

    const fontSize = 14;
    const colWidth = 22;
    let columns = Math.max(6, Math.floor(displayWidth / colWidth));

    interface DropStream {
      x: number;
      y: number;
      speed: number;
      tokens: string[];
      length: number;
      opacity: number;
      brightness: number;
    }

    let drops: DropStream[] = [];

    function initColumns() {
      columns = Math.max(6, Math.floor(displayWidth / colWidth));
      drops = [];

      for (let i = 0; i < columns; i++) {
        const streamLength = Math.floor(Math.random() * 14) + 8;
        const tokens: string[] = [];

        for (let j = 0; j < streamLength; j++) {
          tokens.push(codeTokens[Math.floor(Math.random() * codeTokens.length)]);
        }

        drops.push({
          x: i * colWidth + 6,
          y: (Math.random() * -120) - (Math.random() * displayHeight),
          speed: (0.35 + Math.random() * 0.5) * speed,
          tokens,
          length: streamLength,
          opacity: 0.65 + Math.random() * 0.35,
          brightness: Math.random() > 0.6 ? 1.2 : 1.0,
        });
      }
    }

    resizeCanvas();

    let lastTime = performance.now();
    let lastRenderTime = 0;
    const FRAME_INTERVAL = 45; // ~22fps for smooth, low-CPU ambient animation

    const render = (currentTime: number) => {
      if (currentTime - lastRenderTime < FRAME_INTERVAL) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const delta = Math.min((currentTime - lastTime) / 1000, 0.1);
      lastTime = currentTime;
      lastRenderTime = currentTime;

      // Clean canvas with transparent clearing so background image & glows show through
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.font = `600 ${fontSize}px "JetBrains Mono", "Fira Code", "Courier New", monospace`;
      ctx.textBaseline = 'top';

      for (let i = 0; i < drops.length; i++) {
        const drop = drops[i];

        // Draw each token in the stream
        for (let j = 0; j < drop.length; j++) {
          const tokenY = drop.y - j * (fontSize + 6);
          if (tokenY < -30 || tokenY > displayHeight + 30) continue;

          const isHead = j === 0;
          const isNearHead = j === 1;
          const text = drop.tokens[j] || '1';

          if (isHead) {
            // Radiant bright gold glow at the head of the stream
            ctx.fillStyle = '#FFFDF0';
            ctx.shadowColor = '#FBC44C';
            ctx.shadowBlur = 12;
          } else if (isNearHead) {
            // Bright gold secondary
            ctx.fillStyle = '#F5F19C';
            ctx.shadowColor = '#E8AC44';
            ctx.shadowBlur = 8;
          } else {
            // Highly readable body of code in gold, bronze and amber tones
            const fade = Math.max(0.2, 1 - (j / drop.length) * 0.85);
            const alpha = Math.min(1, fade * drop.opacity * drop.brightness);

            // Vibrant alternating golden, bronze and caramel readable tokens
            if (j % 3 === 0) {
              ctx.fillStyle = `rgba(251, 196, 76, ${alpha})`; // Radiant Gold
            } else if (j % 3 === 1) {
              ctx.fillStyle = `rgba(232, 172, 68, ${alpha})`; // Warm Amber Gold
            } else {
              ctx.fillStyle = `rgba(197, 137, 64, ${alpha})`; // Caramel Bronze
            }
            ctx.shadowBlur = 0;
          }

          // Slightly mutate random token occasionally for digital effect
          if (Math.random() < 0.008) {
            drop.tokens[j] = codeTokens[Math.floor(Math.random() * codeTokens.length)];
          }

          ctx.fillText(text, drop.x, tokenY);
        }

        // Advance drop smoothly at a readable pace
        drop.y += drop.speed * 48 * (delta * 60);

        // Reset drop when fully past screen bottom
        if (drop.y - drop.length * (fontSize + 6) > displayHeight) {
          drop.y = Math.random() * -80 - 20;
          drop.speed = (0.35 + Math.random() * 0.5) * speed;
          drop.brightness = Math.random() > 0.6 ? 1.2 : 1.0;
          // Refresh code tokens
          for (let k = 0; k < drop.length; k++) {
            drop.tokens[k] = codeTokens[Math.floor(Math.random() * codeTokens.length)];
          }
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    let isVisible = true;
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          isVisible = entry.isIntersecting;
          if (isVisible) {
            lastTime = performance.now();
            cancelAnimationFrame(animationFrameId);
            animationFrameId = requestAnimationFrame(render);
          } else {
            cancelAnimationFrame(animationFrameId);
          }
        }
      },
      { threshold: 0.05 }
    );

    if (canvas.parentElement) {
      intersectionObserver.observe(canvas.parentElement);
    }

    // Defer start until browser completes initial paint of hero text
    startTimerId = setTimeout(() => {
      lastTime = performance.now();
      animationFrameId = requestAnimationFrame(render);
    }, 250);

    return () => {
      clearTimeout(startTimerId);
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [opacity, speed, colorMode]);

  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden rounded-[inherit] ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
        style={{ opacity }}
      />
    </div>
  );
};
